import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import Notification from '../Notification';

// ── Types ────────────────────────────────────────────────────────────────────

interface ProductVariation {
  id: string;
  size?: string;
  color?: string;
  sku: string;
  price: number;
  inventory: number;
  lowStockThreshold: number;
}

interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  images: string[];
  variations: ProductVariation[];
  isActive: boolean;
}

interface SimpleStudent {
  id: string;
  name: string;
  email: string | null;
  stripe_customer_id?: string | null;
  billing_mode?: string | null;
}

type PaymentType = 'cash' | 'stripe';
type BuyerMode = 'student' | 'walk_in';

// Stripe minimum charge for card payments
const MIN_CARD_CHARGE_CENTS = 50;

// Same rule as the profile and waiver forms
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const isValidEmail = (email: string) =>
  email.length <= 254 && EMAIL_REGEX.test(email) && !email.includes('..');

// Common misspellings of popular email domains → the domain they meant
const EMAIL_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'icloud.co': 'icloud.com',
  'iclod.com': 'icloud.com',
  'aol.co': 'aol.com',
};

const suggestEmailFix = (email: string): string | null => {
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  const fixed = EMAIL_DOMAIN_TYPOS[email.slice(at + 1).toLowerCase()];
  return fixed ? `${email.slice(0, at)}@${fixed}` : null;
};

// ── Props ────────────────────────────────────────────────────────────────────

interface SellToStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  studioId: string;
  getAuthToken: () => string | null;
  /** Called after a sale is recorded so the parent can refresh inventory. */
  onSaleComplete?: () => void;
}

const CASH_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'apple_pay', label: 'Apple Pay' },
  { value: 'google_pay', label: 'Google Pay' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function SellToStudentModal({
  isOpen,
  onClose,
  product,
  studioId,
  getAuthToken,
  onSaleComplete,
}: SellToStudentModalProps) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Students
  const [students, setStudents] = useState<SimpleStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<SimpleStudent | null>(null);

  // Walk-in (non-student) customer
  const [buyerMode, setBuyerMode] = useState<BuyerMode>('student');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerEmailTouched, setCustomerEmailTouched] = useState(false);
  // null = not checked yet; receipts for cash sales go out through the studio's connected Gmail
  const [emailConnected, setEmailConnected] = useState<boolean | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);
  const [cardApi, setCardApi] = useState<{ stripe: Stripe; elements: StripeElements } | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const isWalkIn = buyerMode === 'walk_in';
  const trimmedCustomerEmail = customerEmail.trim();
  const customerEmailValid = !trimmedCustomerEmail || isValidEmail(trimmedCustomerEmail);
  const customerEmailSuggestion = customerEmailValid ? suggestEmailFix(trimmedCustomerEmail) : null;

  // Product options
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Payment
  const [paymentType, setPaymentType] = useState<PaymentType>('cash');
  const [cashMethod, setCashMethod] = useState('cash');
  const [hasStripeCard, setHasStripeCard] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);

  // Credit
  const [creditAvailableCents, setCreditAvailableCents] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);
  const [creditApplyCents, setCreditApplyCents] = useState(0);

  // Staff discount
  const [discountType, setDiscountType] = useState<'none' | 'percentage' | 'fixed_amount'>('none');
  const [discountPercent, setDiscountPercent] = useState(10);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Notes
  const [notes, setNotes] = useState('');

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title: string;
    type?: 'success' | 'error' | 'general';
  }>({ show: false, message: '', title: '', type: 'success' });

  // ── Fetch students on open ──────────────────────────────────────────────

  const fetchStudents = useCallback(async () => {
    if (!studioId) return;
    setLoadingStudents(true);
    try {
      const response = await fetch(`${backendUrl}/api/supabase/students/${studioId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to load students');
      const data = await response.json();
      const raw = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const list: SimpleStudent[] = raw.map((s: any) => ({
        id: s.id,
        name: s.name,
        email: s.email ?? null,
        stripe_customer_id: s.stripe_customer_id ?? null,
        billing_mode: s.billing_mode ?? null,
      }));
      setStudents(list.sort((a, b) => a.name.localeCompare(b.name)));
    } catch {
      // Students list is best-effort; not a fatal error
    } finally {
      setLoadingStudents(false);
    }
  }, [studioId, backendUrl, getAuthToken]);

  // ── Check card on file when student selected ────────────────────────────

  const checkStripeCard = useCallback(async (student: SimpleStudent) => {
    setLoadingCard(true);
    try {
      const billingRes = await fetch(
        `${backendUrl}/api/stripe/students/${student.id}/billing-info`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      const billingInfo = billingRes.ok ? await billingRes.json() : null;
      const chargeStudentId =
        billingInfo?.billing_primary_id &&
        !billingInfo?.is_billing_primary &&
        (billingInfo?.is_family_billed ||
          billingInfo?.family_consolidated_billing_enabled)
          ? billingInfo.billing_primary_id
          : student.id;

      const [pmRes, creditRes] = await Promise.all([
        fetch(`${backendUrl}/api/stripe/students/${chargeStudentId}/payment-methods`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }),
        fetch(`${backendUrl}/api/stripe/customers/${student.id}/credit-balance`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }),
      ]);
      if (pmRes.ok) {
        const pmData = await pmRes.json();
        setHasStripeCard(!!pmData.hasPaymentMethod);
      } else {
        setHasStripeCard(false);
      }
      if (creditRes.ok) {
        const creditData = await creditRes.json();
        setCreditAvailableCents(creditData.has_credit ? (creditData.balance ?? 0) : 0);
      } else {
        setCreditAvailableCents(0);
      }
    } catch {
      setHasStripeCard(false);
      setCreditAvailableCents(0);
    } finally {
      setLoadingCard(false);
    }
  }, [backendUrl, getAuthToken]);

  // ── Load the studio's Stripe key the first time walk-in mode is used ──

  useEffect(() => {
    if (!isOpen || !isWalkIn || stripePromise || !studioId) return;
    setStripeLoadError(null);
    fetch(`${backendUrl}/api/stripe/publishable-key/${studioId}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.publishableKey) {
          throw new Error(data.error || 'Stripe is not configured for this studio');
        }
        setStripePromise(loadStripe(data.publishableKey));
      })
      .catch((err) => setStripeLoadError(err instanceof Error ? err.message : 'Could not load card form'));
  }, [isOpen, isWalkIn, stripePromise, studioId, backendUrl, getAuthToken]);

  // ── Check whether the studio has email connected (walk-in receipts) ───

  useEffect(() => {
    if (!isOpen || !isWalkIn || emailConnected !== null) return;
    fetch(`${backendUrl}/api/auth/gmail/status`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        // Only show the badge on a definite "not configured"; stay quiet on errors
        if (data) setEmailConnected(!!data.isConfigured);
      })
      .catch(() => {});
  }, [isOpen, isWalkIn, emailConnected, backendUrl, getAuthToken]);

  // ── Reset on open/product change ───────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    fetchStudents();
    setStudentSearch('');
    setSelectedStudent(null);
    setBuyerMode('student');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerEmailTouched(false);
    setCardComplete(false);
    setQuantity(1);
    setPaymentType('cash');
    setCashMethod('cash');
    setHasStripeCard(false);
    setCreditAvailableCents(0);
    setApplyCredit(false);
    setCreditApplyCents(0);
    setDiscountType('none');
    setDiscountPercent(10);
    setDiscountAmount(0);
    setNotes('');
    setError(null);
    setSuccess(false);
    // Auto-select first variation if only one
    if (product?.variations.length === 1) {
      setSelectedVariation(product.variations[0]);
    } else {
      setSelectedVariation(null);
    }
  }, [isOpen, product]);

  // When student changes, check card/credit and reset payment type to cash
  useEffect(() => {
    if (selectedStudent) {
      setPaymentType('cash');
      setApplyCredit(false);
      setCreditApplyCents(0);
      checkStripeCard(selectedStudent);
    } else {
      setHasStripeCard(false);
      setCreditAvailableCents(0);
      setApplyCredit(false);
      setCreditApplyCents(0);
    }
  }, [selectedStudent]);

  // ── Derived ────────────────────────────────────────────────────────────

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => s.name.toLowerCase().includes(q));
  }, [students, studentSearch]);

  const totalAmount = selectedVariation ? selectedVariation.price * quantity : 0;
  const totalAmountCents = Math.round(totalAmount * 100);
  const maxQty = selectedVariation ? selectedVariation.inventory : 0;

  const discountCents = useMemo(() => {
    if (discountType === 'none' || totalAmountCents <= 0) return 0;
    if (discountType === 'percentage') {
      const pct = Math.min(100, Math.max(0, discountPercent));
      return Math.round(totalAmountCents * (pct / 100));
    }
    return Math.min(totalAmountCents, Math.round(Math.max(0, discountAmount) * 100));
  }, [discountType, discountPercent, discountAmount, totalAmountCents]);

  const afterDiscountCents = totalAmountCents - discountCents;

  // Credit-aware derived values
  const maxApplicableCreditCents = Math.min(creditAvailableCents, afterDiscountCents);
  const effectiveCreditCents = applyCredit
    ? Math.min(creditApplyCents, maxApplicableCreditCents)
    : 0;
  const dueAmountCents = afterDiscountCents - effectiveCreditCents;
  const dueAmount = dueAmountCents / 100;

  // Clamp credit when discount changes the max applicable amount
  useEffect(() => {
    if (applyCredit && creditApplyCents > maxApplicableCreditCents) {
      setCreditApplyCents(maxApplicableCreditCents);
    }
  }, [applyCredit, creditApplyCents, maxApplicableCreditCents]);

  const canSubmit = isWalkIn
    ? !!customerName.trim() &&
      customerEmailValid &&
      !!selectedVariation &&
      quantity >= 1 &&
      quantity <= maxQty &&
      (paymentType === 'cash' ||
        (dueAmountCents >= MIN_CARD_CHARGE_CENTS && cardComplete && !!cardApi))
    : !!selectedStudent &&
      !!selectedVariation &&
      quantity >= 1 &&
      quantity <= maxQty &&
      (dueAmountCents === 0 || paymentType === 'cash' || hasStripeCard);

  const switchBuyerMode = (mode: BuyerMode) => {
    setBuyerMode(mode);
    setPaymentType(mode === 'walk_in' ? 'stripe' : 'cash');
    setSelectedStudent(null);
    setStudentSearch('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerEmailTouched(false);
    setCardComplete(false);
    setError(null);
  };

  // ── Submit ─────────────────────────────────────────────────────────────

  const walkInSaleBody = () => ({
    customer_name: customerName.trim(),
    customer_email: trimmedCustomerEmail || undefined,
    variation_id: selectedVariation?.id,
    quantity,
    notes: notes.trim() || undefined,
    discount_type: discountCents > 0 ? discountType : undefined,
    discount_value:
      discountCents > 0 && discountType === 'percentage'
        ? discountPercent
        : discountCents > 0 && discountType === 'fixed_amount'
          ? discountAmount
          : undefined,
  });

  const showWalkInSuccess = (receiptSent: boolean) => {
    setSuccess(true);
    onSaleComplete?.();
    const discountMsg = discountCents > 0
      ? ` ($${(discountCents / 100).toFixed(2)} discount)`
      : '';
    const methodLabel = paymentType === 'cash'
      ? ` via ${CASH_METHODS.find((m) => m.value === cashMethod)?.label ?? cashMethod}`
      : '';
    const receiptMsg = receiptSent && trimmedCustomerEmail
      ? ` — receipt sent to ${trimmedCustomerEmail}`
      : '';
    setNotification({
      show: true,
      title: 'Sale recorded',
      message: `${product?.name} sold to ${customerName.trim()} for $${(dueAmountCents / 100).toFixed(2)}${methodLabel}${discountMsg}${receiptMsg}.`,
      type: 'success',
    });
    setTimeout(() => onClose(), 1800);
  };

  const handleWalkInManualSubmit = async () => {
    if (!canSubmit || !selectedVariation) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/stripe/sell-to-walk-in/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ ...walkInSaleBody(), payment_method: cashMethod }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Sale failed');
      showWalkInSuccess(!!data.receipt_sent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWalkInSubmit = async () => {
    if (paymentType === 'cash') return handleWalkInManualSubmit();
    if (!canSubmit || !selectedVariation || !cardApi) return;
    const card = cardApi.elements.getElement(CardElement);
    if (!card) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const name = customerName.trim();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      };

      const intentRes = await fetch(`${backendUrl}/api/stripe/sell-to-walk-in/intent`, {
        method: 'POST',
        headers,
        body: JSON.stringify(walkInSaleBody()),
      });
      const intentData = await intentRes.json();
      if (!intentRes.ok) throw new Error(intentData.error || 'Sale failed');

      const { error: confirmError } = await cardApi.stripe.confirmCardPayment(intentData.client_secret, {
        payment_method: { card, billing_details: { name } },
      });
      if (confirmError) throw new Error(confirmError.message || 'Card payment failed');

      const completeRes = await fetch(`${backendUrl}/api/stripe/sell-to-walk-in/complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ payment_intent_id: intentData.payment_intent_id }),
      });
      const completeData = await completeRes.json();
      if (!completeRes.ok) {
        throw new Error(
          `Card was charged, but the sale could not be finalized: ${completeData.error || 'unknown error'}`
        );
      }

      // Stripe sends the card receipt itself via receipt_email
      showWalkInSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isWalkIn) return handleWalkInSubmit();
    if (!canSubmit || !selectedStudent || !selectedVariation) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/stripe/sell-to-student`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          student_id: selectedStudent.id,
          variation_id: selectedVariation.id,
          quantity,
          payment_type: paymentType,
          cash_payment_method: cashMethod,
          notes: notes.trim() || undefined,
          credit_amount_cents: effectiveCreditCents > 0 ? effectiveCreditCents : undefined,
          discount_type: discountCents > 0 ? discountType : undefined,
          discount_value:
            discountCents > 0 && discountType === 'percentage'
              ? discountPercent
              : discountCents > 0 && discountType === 'fixed_amount'
                ? discountAmount
                : undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Sale failed');
      }

      // Auto-send receipt for cash payments if student has an email (and something was actually charged)
      if (paymentType === 'cash' && data.payment_id && data.student_email) {
        try {
          await fetch(`${backendUrl}/api/manual-payments/send-receipt`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              payment_ids: [data.payment_id],
              student_id: selectedStudent.id,
              student_email: data.student_email,
            }),
          });
        } catch {
          // Receipt failure is non-fatal
        }
      }

      setSuccess(true);
      onSaleComplete?.();
      const discountMsg = discountCents > 0
        ? ` ($${(discountCents / 100).toFixed(2)} discount)`
        : '';
      const creditMsg = effectiveCreditCents > 0
        ? ` ($${(effectiveCreditCents / 100).toFixed(2)} credit applied)`
        : '';
      const receiptMsg = paymentType === 'cash' && data.student_email ? ' — receipt sent' : '';
      const chargedAmount = dueAmountCents / 100;
      setNotification({
        show: true,
        title: 'Sale recorded',
        message: `${product?.name} sold to ${selectedStudent.name} for $${chargedAmount.toFixed(2)}${discountMsg}${creditMsg}${receiptMsg}.`,
        type: 'success',
      });

      // Close after a short pause so the success notification is visible
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (!isOpen || !product) return null;

  const variationLabel = (v: ProductVariation) => {
    const parts: string[] = [];
    if (v.size) parts.push(v.size);
    if (v.color) parts.push(v.color);
    return parts.length ? parts.join(' / ') : v.sku;
  };

  return (
    <>
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', title: '', type: 'success' })}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 backdrop-blur-sm p-4">
        <div className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col max-h-[92vh]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isWalkIn ? 'Sell to Customer' : 'Sell to Student'}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{product.name}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

            {/* ── Variation selector ─────────────────────────────────── */}
            {product.variations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Variation <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {product.variations.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        setSelectedVariation(v);
                        if (quantity > v.inventory) setQuantity(Math.max(1, v.inventory));
                      }}
                      disabled={v.inventory === 0}
                      className={`rounded-lg border p-3 text-left text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        selectedVariation?.id === v.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400 ring-1 ring-indigo-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{variationLabel(v)}</div>
                      <div className="mt-0.5 text-gray-500 dark:text-gray-400">
                        ${v.price.toFixed(2)} · {v.inventory} in stock
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Single variation info */}
            {product.variations.length === 1 && selectedVariation && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between text-sm">
                <span className="font-medium text-gray-900 dark:text-white">
                  {variationLabel(selectedVariation)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  ${selectedVariation.price.toFixed(2)} · {selectedVariation.inventory} in stock
                </span>
              </div>
            )}

            {/* ── Quantity ────────────────────────────────────────────── */}
            {selectedVariation && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="h-9 w-9 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 text-lg font-medium"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={maxQty}
                    value={quantity}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) setQuantity(Math.min(maxQty, Math.max(1, v)));
                    }}
                    className="w-16 text-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    disabled={quantity >= maxQty}
                    className="h-9 w-9 flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 text-lg font-medium disabled:opacity-40"
                  >
                    +
                  </button>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white ml-2">
                    = ${totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* ── Student search ──────────────────────────────────────── */}
            {!isWalkIn && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Student <span className="text-red-500">*</span>
              </label>

              {selectedStudent ? (
                <div className="flex items-center justify-between rounded-lg border border-indigo-300 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedStudent.name}</p>
                    {selectedStudent.email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedStudent.email}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedStudent(null); setStudentSearch(''); }}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search students…"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  {studentSearch.trim() && (
                    loadingStudents ? (
                      <div className="mt-2 text-center py-4">
                        <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                      </div>
                    ) : (
                      <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredStudents.length === 0 ? (
                          <li className="px-3 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                            No students match
                          </li>
                        ) : (
                          filteredStudents.map((s) => (
                            <li key={s.id}>
                              <button
                                type="button"
                                onClick={() => setSelectedStudent(s)}
                                className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <span className="text-sm text-gray-900 dark:text-white">{s.name}</span>
                                {s.stripe_customer_id ? (
                                  <CreditCardIcon className="h-4 w-4 text-indigo-400 flex-shrink-0" title="Has Stripe account" />
                                ) : null}
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => switchBuyerMode('walk_in')}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    <UserIcon className="h-3.5 w-3.5" />
                    Not a student? Enter customer details
                  </button>
                </>
              )}
            </div>
            )}

            {/* ── Walk-in customer ────────────────────────────────────── */}
            {isWalkIn && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => switchBuyerMode('student')}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Sell to a student instead
                    </button>
                  </div>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    maxLength={120}
                    placeholder="Full name"
                    className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  {emailConnected === false && (
                    <div className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-200 dark:ring-amber-700">
                      <ExclamationCircleIcon className="h-3.5 w-3.5" />
                      <span>
                        Connect your studio email in{' '}
                        <Link to="/app/settings/integrations" className="underline hover:no-underline">
                          Settings
                        </Link>
                      </span>
                    </div>
                  )}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-gray-400 font-normal">(optional, for receipt)</span>
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    onBlur={() => setCustomerEmailTouched(true)}
                    maxLength={254}
                    placeholder="name@example.com"
                    aria-invalid={customerEmailTouched && !customerEmailValid}
                    className={`block w-full rounded-md border bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      customerEmailTouched && !customerEmailValid
                        ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 dark:border-gray-600 focus:ring-indigo-500'
                    }`}
                  />
                  {customerEmailTouched && !customerEmailValid && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Enter a valid email address, like name@example.com.
                    </p>
                  )}
                  {customerEmailSuggestion && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      Did you mean{' '}
                      <button
                        type="button"
                        onClick={() => setCustomerEmail(customerEmailSuggestion)}
                        className="font-medium underline hover:no-underline"
                      >
                        {customerEmailSuggestion}
                      </button>
                      ?
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { value: 'stripe', label: 'Card', Icon: CreditCardIcon },
                      { value: 'cash', label: 'Cash / Manual', Icon: BanknotesIcon },
                    ] as const).map(({ value, label, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setPaymentType(value);
                          // The card field remounts empty when switching back to Card
                          setCardComplete(false);
                        }}
                        className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                          paymentType === value
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500'
                            : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentType === 'cash' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Payment type
                    </label>
                    <select
                      value={cashMethod}
                      onChange={(e) => setCashMethod(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white py-2 px-3"
                    >
                      {CASH_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Credit Card <span className="text-red-500">*</span>
                  </label>
                  {stripeLoadError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{stripeLoadError}</p>
                  ) : stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <WalkInCardField
                        disabled={isSubmitting || success}
                        onReady={setCardApi}
                        onChange={setCardComplete}
                      />
                    </Elements>
                  ) : (
                    <div className="flex justify-center py-3">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                    </div>
                  )}
                  {selectedVariation && dueAmountCents > 0 && dueAmountCents < MIN_CARD_CHARGE_CENTS && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Card payments must be at least $0.50.
                    </p>
                  )}
                </div>
                )}
              </div>
            )}

            {/* ── Staff discount ─────────────────────────────────────── */}
            {(selectedStudent || isWalkIn) && selectedVariation && totalAmountCents > 0 && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-4 py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Discount <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  {discountCents > 0 && (
                    <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      −${(discountCents / 100).toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'none', label: 'None' },
                    { value: 'percentage', label: '% Off' },
                    { value: 'fixed_amount', label: '$ Off' },
                  ] as const).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setDiscountType(option.value)}
                      className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                        discountType === option.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500'
                          : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {discountType === 'percentage' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Percent off
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={discountPercent}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val)) setDiscountPercent(Math.min(100, Math.max(0, val)));
                        }}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pr-8 pl-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">%</span>
                    </div>
                  </div>
                )}

                {discountType === 'fixed_amount' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Amount off
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                      <input
                        type="number"
                        min={0.01}
                        max={totalAmount}
                        step={0.01}
                        value={discountAmount || ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) setDiscountAmount(Math.min(totalAmount, Math.max(0, val)));
                        }}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-7 pr-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-1 text-sm pt-1 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
                    <span>Subtotal</span>
                    <span>${totalAmount.toFixed(2)}</span>
                  </div>
                  {discountCents > 0 && (
                    <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                      <span>Discount</span>
                      <span>−${(discountCents / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between font-medium text-gray-900 dark:text-white">
                    <span>After discount</span>
                    <span>${(afterDiscountCents / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Account credit ──────────────────────────────────────── */}
            {selectedStudent && creditAvailableCents > 0 && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={applyCredit}
                      onChange={(e) => {
                        setApplyCredit(e.target.checked);
                        if (e.target.checked) {
                          setCreditApplyCents(maxApplicableCreditCents);
                        } else {
                          setCreditApplyCents(0);
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      Apply account credit
                    </span>
                  </label>
                  <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    ${(creditAvailableCents / 100).toFixed(2)} available
                  </span>
                </div>

                {applyCredit && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400 shrink-0">Apply:</span>
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">$</span>
                        <input
                          type="number"
                          min="0.01"
                          max={(maxApplicableCreditCents / 100).toFixed(2)}
                          step="0.01"
                          value={(creditApplyCents / 100).toFixed(2)}
                          onChange={(e) => {
                            const val = Math.round(parseFloat(e.target.value || '0') * 100);
                            setCreditApplyCents(Math.min(maxApplicableCreditCents, Math.max(0, val)));
                          }}
                          className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-7 pr-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setCreditApplyCents(maxApplicableCreditCents)}
                        className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline whitespace-nowrap shrink-0"
                      >
                        Use max
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-emerald-200 dark:border-emerald-700">
                      <span className="text-gray-500 dark:text-gray-400">Due after credit:</span>
                      <span className={`font-semibold ${dueAmountCents === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>
                        {dueAmountCents === 0 ? 'Nothing due' : `$${dueAmount.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Payment type ────────────────────────────────────────── */}
            {selectedStudent && dueAmountCents > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {/* Cash tab */}
                  <button
                    type="button"
                    onClick={() => setPaymentType('cash')}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                      paymentType === 'cash'
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <BanknotesIcon className="h-5 w-5" />
                    Cash / Manual
                  </button>

                  {/* Stripe tab */}
                  <button
                    type="button"
                    onClick={() => hasStripeCard && setPaymentType('stripe')}
                    disabled={loadingCard || !hasStripeCard}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                      paymentType === 'stripe'
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500'
                        : hasStripeCard
                        ? 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                        : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 opacity-50'
                    }`}
                  >
                    {loadingCard ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                    ) : (
                      <CreditCardIcon className="h-5 w-5" />
                    )}
                    Card on File
                    {!loadingCard && !hasStripeCard && selectedStudent && (
                      <span className="ml-1 text-xs">(none)</span>
                    )}
                  </button>
                </div>

                {/* Cash sub-method */}
                {paymentType === 'cash' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Payment type
                    </label>
                    <select
                      value={cashMethod}
                      onChange={(e) => setCashMethod(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white py-2 px-3"
                    >
                      {CASH_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {paymentType === 'stripe' && hasStripeCard && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    The student's card on file will be charged. Stripe will send them a receipt automatically.
                  </p>
                )}
              </div>
            )}

            {/* ── Notes ───────────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={300}
                placeholder="Any notes about this sale…"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
            </div>

            {/* ── Error ───────────────────────────────────────────────── */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* ── Success ─────────────────────────────────────────────── */}
            {success && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm text-green-700 dark:text-green-300">Sale recorded successfully!</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting || success}
              className="flex-1 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? 'Processing…'
                : dueAmountCents === 0
                ? 'Confirm Sale (credit only)'
                : `Charge $${dueAmount.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Card field (must render inside <Elements>) ───────────────────────────────

function WalkInCardField({
  disabled,
  onReady,
  onChange,
}: {
  disabled: boolean;
  onReady: (api: { stripe: Stripe; elements: StripeElements } | null) => void;
  onChange: (complete: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  useEffect(() => {
    onReady(stripe && elements ? { stripe, elements } : null);
    return () => onReady(null);
  }, [stripe, elements, onReady]);

  return (
    <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5">
      <CardElement
        onChange={(e) => onChange(e.complete)}
        options={{
          disabled,
          style: {
            base: {
              fontSize: '14px',
              color: isDark ? '#ffffff' : '#111827',
              '::placeholder': { color: '#9ca3af' },
            },
            invalid: { color: '#dc2626' },
          },
        }}
      />
    </div>
  );
}
